import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  AiFillDislike,
  AiFillLike,
  AiOutlineDislike,
  AiOutlineLike,
} from "react-icons/ai";
import { TbFlag, TbFlagFilled } from "react-icons/tb";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import { MdAccessTimeFilled } from "react-icons/md";
import { FaRegStar, FaStar } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

const TOASTER_ID = "global-toaster"; // <-- ensure a single ToastContainer uses this id

const AllCompanyReviews = ({ companyName }) => {
  const [reviews, setReviews] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  // engagements[reviewId] = { like: bool, dislike: bool, reported: bool }
  const [engagements, setEngagements] = useState({});
  const [engagementsLoaded, setEngagementsLoaded] = useState(false);
  const inFlight = useRef(new Set());
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    // reset when company changes (keep engagements cache persistent)
    setReviews([]);
    setLastKey(null);
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  // Load cached engagements once
  useEffect(() => {
    try {
      const cached = localStorage.getItem("userEngagements");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === "object") {
          setEngagements(parsed);
        }
      }
    } catch (e) {
      console.error("Error loading engagements cache:", e);
    } finally {
      setEngagementsLoaded(true);
    }
  }, []);

  // Persist engagements when changed (after initial load)
  useEffect(() => {
    if (!engagementsLoaded) return;
    try {
      localStorage.setItem("userEngagements", JSON.stringify(engagements));
    } catch (e) {
      console.error("Error saving engagements cache:", e);
    }
  }, [engagements, engagementsLoaded]);

  const fetchReviews = async (key = null) => {
    try {
      setLoadingMore(true);
      const response = await axios.get(`${getApiBaseUrl()}/company_reviews`, {
        params: {
          company_name: companyName ?? "",
          ...(key ? { last_key: typeof key === "string" ? key : JSON.stringify(key) } : {}),
        },
      });
      const { company_reviews = [], last_key = null } = response.data;
      setReviews((prev) => [...prev, ...company_reviews]);
      setLastKey(last_key);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Small "flag" toast (deduped by toastId and targeted to a single container)
  const showFlag = (message, toastId) => {
    const base = {
      position: "top-center",
      autoClose: 1600,
      hideProgressBar: true,
      closeButton: false,
      draggable: false,
      className: "flag-toast",
      bodyClassName: "flag-toast-body",
      icon: <span className="flag-dot" />,
      containerId: TOASTER_ID,
      toastId,
    };
    if (toast.isActive(toastId)) {
      toast.update(toastId, { render: message, ...base });
    } else {
      toast(message, base);
    }
  };

  const handleEngagement = (review, type) => {
    const reviewId = review.review_id;

    // Prevent duplicate actions and multiple concurrent API calls
    const prevE = engagements[reviewId] || { like: false, dislike: false, reported: false };
    // Disable re-clicking the same action and prevent switching between like/dislike; report remains one-time
    if ((type === "like" && (prevE.like || prevE.dislike)) || 
        (type === "dislike" && (prevE.dislike || prevE.like)) || 
        (type === "report" && prevE.reported)) {
      return;
    }
    if (inFlight.current.has(reviewId)) return;
    inFlight.current.add(reviewId);

    // Build next engagement state (no toggle-off; allow switching like<->dislike; report is one-time)
    const nextE = { ...prevE };
    if (type === "like") {
      nextE.like = true; // no toggle-off by same click
      nextE.dislike = false; // exclusive when turning on
      showFlag("Thanks for the like!", `like-${reviewId}`);
    } else if (type === "dislike") {
      nextE.dislike = true; // no toggle-off by same click
      nextE.like = false; // exclusive when turning on
      showFlag("Thanks for the feedback!", `dislike-${reviewId}`);
    } else if (type === "report") {
      nextE.reported = true;
      showFlag("Review reported", `report-${reviewId}`);
    }

    // Optimistic cache update
    setEngagements((prev) => ({ ...prev, [reviewId]: nextE }));

    // Prepare payload (report sends like/dislike false; only report true)
    const isReport = type === "report";
    const payload = {
      manager_id: review.manager_id,
      manager_full_name: review.manager_full_name,
      review_id: review.review_id,
      like: isReport ? false : nextE.like,
      dislike: isReport ? false : nextE.dislike,
      reported: isReport ? true : false,
    };

    (async () => {
      try {
        const { data } = await axios.put(`${getApiBaseUrl()}/review_engagement`, payload);
        const serverProfile = data?.review_profile;
        if (serverProfile) {
          setReviews((prev) =>
            prev.map((r) =>
              r.review_id === reviewId ? { ...r, likes: serverProfile.likes, dislikes: serverProfile.dislikes } : r
            )
          );
        }
      } catch (error) {
        console.error("Error updating engagement:", error);
        // rollback
        setEngagements((cur) => ({ ...cur, [reviewId]: prevE }));
        toast.error("Couldn't save your action. Try again?", {
          position: "top-center",
          autoClose: 1800,
          containerId: TOASTER_ID,
        });
      } finally {
        inFlight.current.delete(reviewId);
      }
    })();
  };

  return (
    <>
      {/* Flag toast styles (lightweight) */}
      <style>{`
        .flag-toast {
          background:#fff !important;
          color:#111 !important;
          border-left:4px solid #16a34a !important;
          border-radius:10px !important;
          box-shadow:0 6px 18px rgba(0,0,0,.12) !important;
          padding:.75rem 1rem !important;
          min-width:220px;
        }
        .flag-toast-body { font-weight:600; }
        .flag-dot {
          width:10px;height:10px;display:inline-block;border-radius:50%;
          background:#16a34a;margin-right:.5rem;
        }
      `}</style>

      {reviews.map((review) => {
        const rid = review?.review_id;
        const baseLikes = Number(review?.likes) || 0;
        const baseDislikes = Number(review?.dislikes) || 0;
        const e = engagements[rid] || { like: false, dislike: false, reported: false };

        // Derived counts for instant UI change (optimistic)
        const likesDisplay = baseLikes;
        const dislikesDisplay = baseDislikes;

        return (
          <div key={rid} className="my-lg-4 my-md-4 my-3">
            <div>
              <div className="d-flex justify-content-end pb-1">
                <div className="me-2">
                  {review?.is_verified ? (
                    <div className="verified-tooltip-trending ms-2">
                      <span className="verified-badge-trending">Verified</span>
                      <span className="verified-tooltip-text">
                        Verified reviews are submitted by individuals who have confirmed their employment.
                      </span>
                    </div>
                  ) : (
                    <div className="unverified-tooltip-trending ms-2">
                      <span className="unverified-badge-trending">Unverified</span>
                      <span className="unverified-tooltip-text">
                        unverified review will not count towards the manager's official rating.
                      </span>
                    </div>
                  )}
                </div>
                <p className="mb-0">
                  <MdAccessTimeFilled color="#9C9C9C" />
                </p>
                <p className="ps-1 access_time_now pt-1 mb-0">{review?.creation_time}</p>
              </div>
            </div>

            <div className="white_reviews_box">
              <div className="py-3 px-lg-4 px-md-3 px-3">
                <div className="row">
                  <div className="col-lg-12 col-md-12 col-12">
                    <div>
                      <div className="d-flex justify-content-between">
                        <div>
                          <Link
                            style={{ textDecoration: "none" }}
                            to={`/company-profile?name=${encodeURIComponent(review?.company_name)}&id=${review?.company_id}`}
                          >
                            <h4 className="all_reviews_h4">{review?.company_name}</h4>
                          </Link>
                        </div>
                        <div>
                          <div>
                            {/* Like */}
                            {e.like ? (
                              <AiFillLike
                                fontSize={22}
                                color="#151515"
                                onClick={() => handleEngagement(review, "like")}
                                style={{ cursor: (e.like || e.dislike) ? 'not-allowed' : 'pointer' }} 
                                aria-label="Unlike"
                              />
                            ) : (
                              <AiOutlineLike
                                fontSize={22}
                                color="#151515"
                                onClick={() => handleEngagement(review, "like")}
                                style={{ cursor: (e.like || e.dislike) ? 'not-allowed' : 'pointer' }}
                                title="Like"
                                aria-label="Like"
                              />
                            )}
                            <span className="add_review_p mx-1 fw-bold">{likesDisplay}</span>

                            {/* Dislike */}
                            {e.dislike ? (
                              <AiFillDislike
                                fontSize={25}
                                color="#151515"
                                className="ms-2"
                                onClick={() => handleEngagement(review, "dislike")}
                                style={{ cursor: (e.dislike || e.like) ? 'not-allowed' : 'pointer' }}
                                aria-label="Remove dislike"
                              />
                            ) : (
                              <AiOutlineDislike
                                fontSize={25}
                                color="#151515"
                                className="ms-2"
                                onClick={() => handleEngagement(review, "dislike")}
                                style={{ cursor: (e.dislike || e.like) ? 'not-allowed' : 'pointer' }}
                                title="Dislike"
                                aria-label="Dislike"
                              />
                            )}
                            <span className="add_review_p ms-1 fw-bold">{dislikesDisplay}</span>
                          </div>
                        </div>
                      </div>

                      <div className="d-lg-flex d-md-flex">
                        <div>
                          <Link
                            style={{ textDecoration: "none" }}
                            to={`/manager?name=${encodeURIComponent(review?.manager_full_name)}&id=${review?.manager_id}`}
                          >
                            <h6 className="all_reviews_h6_details fw-bold">{review?.manager_full_name}</h6>
                          </Link>
                        </div>
                        <div className="border_difference_tags_difference ms-2 me-2"></div>
                        <div>
                          <h6 className="all_reviews_h6_details">{review?.manager_position}</h6>
                        </div>
                        <div className="border_difference_tags_difference ms-2 me-2"></div>
                        <div>
                          <h6 className="all_reviews_h6_details">{review?.location}</h6>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2">
                        {Array.from({ length: 5 }, (_, i) =>
                          i < review?.review_rating ? <FaStar key={i} color="#000000" /> : <FaRegStar key={i} />
                        )}
                      </p>
                    </div>

                    <div className="d-flex align-items-center justify-content-between pt-lg-0 pt-md-0 pt-3">
                      <div>
                        <h5 className="mb-0 tags_manager_profile">Tags</h5>
                      </div>
                    </div>

                    <div className="d-lg-flex d-md-flex align-items-center">
                      <div>
                        <div className="d-flex flex-wrap gap-2 pt-2">
                          {review?.positive_tags?.map((tag) => (
                            <div
                              key={tag}
                              className="px-3 py-1"
                              style={{
                                backgroundColor: "#EDFDED",
                                color: "#2C2C2C",
                                cursor: "pointer",
                                borderRadius: "30px",
                                fontSize: "0.85em",
                                height: "28px",
                              }}
                            >
                              {tag}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="border_difference_tags ms-2 mt-2"></div>
                      <div>
                        <div className="d-flex flex-wrap gap-2 pt-2 ps-2">
                          {review?.negative_tags?.map((tag) => (
                            <div
                              key={tag}
                              className="px-3 py-1"
                              style={{
                                backgroundColor: "#FFD0CA",
                                color: "#2C2C2C",
                                cursor: "pointer",
                                borderRadius: "30px",
                                fontSize: "0.85em",
                                height: "28px",
                              }}
                            >
                              {tag}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="manager_reviews_box p-3 mt-lg-4 mt-md-3 mt-3">
                      <p className="manager_reviews_box_p">{review?.review}</p>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="d-flex justify-content-end pt-2">
                    <div>
                      {e.reported ? (
                        <TbFlagFilled
                          fontSize={25}
                          color="#151515"
                          onClick={() => handleEngagement(review, "report")}
                          style={{ cursor: "pointer" }}
                          title="Unreport"
                          aria-label="Unreport"
                        />
                      ) : (
                        <TbFlag
                          fontSize={25}
                          color="#151515"
                          onClick={() => handleEngagement(review, "report")}
                          style={{ cursor: "pointer" }}
                          title="Report"
                          aria-label="Report"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* View More Button */}
      {lastKey && (
        <div className="text-center my-4 pt-4">
          <button className="view_more_btn" onClick={() => fetchReviews(lastKey)} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "View More"}
          </button>
        </div>
      )}
    </>
  );
};

export default AllCompanyReviews;